import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Camera, Trash2, Loader2, CheckCircle2, ImageUp } from "lucide-react";

export function CompletionPhoto({ errand, isRunner, isRequester, onChange }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const hasPhoto = !!errand.completion_photo;

  const loadPhoto = async () => {
    if (!hasPhoto) { setBlobUrl(null); return; }
    setLoading(true);
    try {
      const res = await api.get(`/errands/${errand.id}/completion-photo`, { responseType: "blob" });
      setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch (e) { console.error("Failed to load completion photo", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPhoto(); return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }; /* eslint-disable-next-line */ }, [errand.completion_photo?.path]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/errands/${errand.id}/completion-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Completion photo uploaded");
      await onChange?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/errands/${errand.id}/completion-photo`);
      if (blobUrl) { URL.revokeObjectURL(blobUrl); setBlobUrl(null); }
      toast.success("Photo removed — completion confirmed. Thank you!");
      await onChange?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Runner: show uploader when there's no photo yet
  const showUploader = isRunner && !hasPhoto && ["in_progress", "completed"].includes(errand.status);

  if (!hasPhoto && !showUploader) {
    if (isRequester && errand.completion_confirmed) {
      return (
        <div data-testid="completion-confirmed" className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6 flex items-center gap-2 text-[#2F8F6E] font-bold">
          <CheckCircle2 size={18} /> You confirmed safe completion.
        </div>
      );
    }
    return null;
  }

  return (
    <div data-testid="completion-photo-panel" className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6">
      <h2 className="font-heading font-extrabold text-lg text-[#1A1D20] mb-1 flex items-center gap-2">
        <Camera size={18} className="text-primary" /> Proof of completion
        {showUploader && <span className="text-xs font-semibold text-[#9CA3AF]">(optional)</span>}
      </h2>

      {showUploader && (
        <>
          <p className="text-sm text-[#6B7280] mb-4">Snap or upload a photo if you can — it's optional. No camera? Just tap <span className="font-semibold text-[#4A5361]">"Mark completed"</span> to finish the task.</p>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={upload} className="hidden" data-testid="completion-file-input" />
          <Button data-testid="upload-completion-btn" onClick={() => fileRef.current?.click()} disabled={busy}
            className="w-full rounded-full h-12 font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
            {busy ? <Loader2 className="animate-spin" /> : <><ImageUp size={18} className="mr-2" /> Take / upload photo</>}
          </Button>
        </>
      )}

      {hasPhoto && (
        <>
          <p className="text-sm text-[#6B7280] mb-3">
            {isRequester ? "Your runner shared this proof. Delete it to confirm safe completion." : "Shared with the requester as proof of completion."}
          </p>
          <div className="rounded-2xl overflow-hidden border border-gray-100 bg-muted mb-4 min-h-[160px] flex items-center justify-center">
            {loading ? <Loader2 className="animate-spin text-[#9CA3AF]" /> :
              blobUrl ? <img data-testid="completion-photo-img" src={blobUrl} alt="Proof of completion" className="w-full max-h-80 object-contain" />
              : <span className="text-sm text-[#9CA3AF] py-10">Preview unavailable</span>}
          </div>
          {isRequester && (
            <Button data-testid="delete-completion-btn" onClick={confirmDelete} disabled={busy}
              className="w-full rounded-full h-12 font-bold bg-accent hover:opacity-90 text-white active:scale-95 transition-transform">
              {busy ? <Loader2 className="animate-spin" /> : <><Trash2 size={17} className="mr-2" /> Delete photo & confirm completion</>}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
